import type {
  AudioFontSchemaType,
  AudioSchemaType,
  VideoFontSchemaType,
  VideoSchemaType,
} from '@vedalib/editor/lib/brand'
import { FileUsageSource } from '@vedalib/editor/lib/files'
import { useInViewport } from 'ahooks'
import cn from 'classnames'
import type { CSSProperties } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ReactPlayer from 'react-player'
import screenfull from 'screenfull'
import { useContextSelector } from 'use-context-selector'
import { FileTypeEnum } from 'utils'

import type { ElementFontCss, ElementStyleCss } from 'services/Branding/types'
import { MediaContext } from 'services/Media/MediaProvider'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getDeviceMode } from 'services/Store/Project/selectors'
import { getCurrentLocale } from 'services/Translation/i18n'
import { useUnmountMedia } from 'services/lms/hooks/useUnmountMedia'
import { testProps } from 'utils/test/qaData'
import { useReducerWithMiddleware } from 'utils/useReducerWithMiddleware'

import Controls from './Controls'
import MobileControls from './Controls/MobileControls'
import { MediaControlType } from './Controls/types'
import * as s from './MediaPlayer.module.scss'
import { DEFAULT_STYLES } from './constants'
import { encodeSilentBuffer, getDurationWithBreakpoints, isYouTubeVideo } from './helper'
import { usePlayerHotKeys } from './hooks'
import Play from './play.svg?react'
import { showCc, setFullscreen } from './store/actions'
import {
  play,
  pause,
  setProgress,
  togglePlay,
  update,
  seekTo,
  setDuration,
  setVolume,
} from './store/actions'
import {
  fullscreenMiddleware,
  reactPlayerAnalyticMiddleware,
  reactPlayerMiddleware,
} from './store/middlewares'
import reducer from './store/reducer'
import type { Actions, IState } from './store/types'
import PlayStyled from './styled/PlayStyled'
import type { IMediaPlayerBaseProps, IProgressChange } from './types'

const IS_IOS = /iPhone/.test(navigator.userAgent)

const percentPlayed = (played: number, duration: number) => {
  if (!duration || duration <= 0) {
    return 0
  }
  return (played / duration) * 100
}

export interface IMediaPlayerProps extends IMediaPlayerBaseProps {
  url: string | null
  type: FileTypeEnum.VIDEO | FileTypeEnum.AUDIO
  autoPlay?: boolean
  controls?: boolean
  cover?: { path?: string; overlay?: string; objectFit?: string }
  volume?: number
  start?: number
  loop?: boolean
  styles?: ElementStyleCss<VideoSchemaType> | ElementStyleCss<AudioSchemaType>
  fonts?: ElementFontCss<AudioFontSchemaType | VideoFontSchemaType>
  isEditor?: boolean
  playbackRate?: number
  onDuration?: (duration: number, d: number) => void
  onPlay?: (playSecond: number, duration: number) => void
  onPause?: (playSecond: number, duration: number) => void
  onSeek?: (seek: { seekFrom: number; seekTo: number; duration: number }) => void
  onReady?: () => void
  onProgress?: (progress: number, duration: number) => void
  full?: boolean
  accessibility?: string
  captions?: string
  enableCCByDefault?: boolean
  sourceType?: FileUsageSource
  breakpoints?: [number, number]
  duration?: number | null
  isV2?: boolean
  disableSeeking?: boolean
  elementId?: string
  savedMaxPlayedSeconds?: number
  onSavePlaybackPosition?: (maxPlayedSeconds: number) => void
  displayPlayback?: boolean
  isCourse?: boolean
}

let blob: string = ''

const MediaPlayer: React.FC<IMediaPlayerProps> = ({
  url = '',
  type,
  styles = DEFAULT_STYLES,
  cover,
  isEditor,
  onDuration,
  onPlay,
  onReady,
  fonts,
  start = 0,
  autoPlay = false,
  loop = false,
  controls = true,
  playbackRate = 1,
  volume = 1,
  onProgress,
  full = false,
  accessibility,
  captions,
  enableCCByDefault,
  sourceType,
  onPause,
  breakpoints,
  duration: durationProps,
  isV2,
  onSeek,
  disableSeeking,
  elementId,
  savedMaxPlayedSeconds,
  onSavePlaybackPosition,
  displayPlayback,
  isCourse = false,
  ...rest
}) => {
  const player = useRef<ReactPlayer>(null)
  const ref = useRef<HTMLDivElement>(null)
  const ccPayerRef = useRef<ReactPlayer>(null)
  const [inViewport, ratio] = useInViewport(ref, { threshold: [0, 1] })

  const [iosFullScreen, setIosFullScreen] = useState(false)
  const { clearActiveMediaPlayer, setActiveMediaPlayer, activeMedia } = useContextSelector(
    MediaContext,
    (context) => context,
  )
  const locale = getCurrentLocale()
  const device = useProjectContext(getDeviceMode)

  const [state, dispatch] = useReducerWithMiddleware<IState, Actions>(
    reducer,
    {
      started: false,
      playing: false,
      muted: false,
      loop,
      playbackRate,
      start,
      volume,
      duration: 60,
      showCc: enableCCByDefault || false,
      maxPlayedSeconds: savedMaxPlayedSeconds,
    },
    [
      fullscreenMiddleware(ref),
      reactPlayerMiddleware(player),
      reactPlayerAnalyticMiddleware(onPlay, onPause, onSeek),
    ],
  )
  const duration = state.duration
  const mainStyles = useMemo(
    () => ({ ...styles.player, ...fonts?.player, ...styles.effects, ...styles.border }),
    [fonts?.player, styles.player, styles.effects, styles.border],
  )

  const handleKeyDown = usePlayerHotKeys(
    dispatch,
    state,
    duration,
    type,
    !isEditor && disableSeeking,
  )
  const showCover = Boolean(cover?.path && !state.playing && !state.played)
  const showMask = !state.playing
  const innerBorderRadius = parseInt(String(styles?.border?.borderRadius || '6px')) - 2
  const isSource = sourceType === FileUsageSource.file

  const track = useMemo(
    () => ({
      kind: 'subtitles',
      src: captions || '',
      srcLang: locale.toLowerCase(),
      default: true,
      label: locale,
    }),
    [captions, locale],
  )

  const ST: CSSProperties = {
    position: 'absolute',
    backgroundColor: 'transparent',
    zIndex: 3,
    inset: type === FileTypeEnum.VIDEO ? 0 : -50,
  }

  const ccData = useMemo(() => {
    return {
      file: {
        attributes: { crossOrigin: 'true' },
        tracks: [track],
        forceVideo: true,
      },
    }
  }, [track])

  const setIosFullScreenMemo = useCallback(() => setIosFullScreen((prev) => !prev), [])
  const handleFullScreenIos = IS_IOS ? setIosFullScreenMemo : undefined

  const handleTogglePlay = () => dispatch(togglePlay())

  const handlePlay = () => {
    if (activeMedia?.instance !== ref.current) {
      setActiveMediaPlayer?.(ref.current, () => dispatch(pause()))
    }

    dispatch(play())
  }

  const handlePause = () => dispatch(pause())

  const onDurationChange = useCallback(
    (d: number) => {
      const newDuration = isSource ? getDurationWithBreakpoints(d, breakpoints) : d
      onDuration?.(newDuration, d)
      dispatch(setDuration(newDuration))
    },
    [isSource, breakpoints, onDuration, dispatch],
  )
  const handleProgress = (progress: IProgressChange) => {
    if (!isEditor && disableSeeking) {
      const maxAllowed = (state.maxPlayedSeconds ?? 0) + start
      if (progress.playedSeconds > maxAllowed + 2) {
        player.current?.seekTo(maxAllowed, 'seconds')
        return
      }
    }

    ccPayerRef.current?.seekTo(progress.playedSeconds, 'seconds')
    // TODO: YouTube API reports duration ~1s longer than actual playable time,
    // so we subtract 1s to allow reaching 100% progress
    const effectiveDuration = isYouTubeVideo(url) ? duration - 1 : duration
    const percent = percentPlayed(progress.playedSeconds, effectiveDuration)
    onProgress?.(percent, duration)
    return dispatch(setProgress({ ...progress, duration }))
  }

  const handleOnReady = () => {
    // TODO: tmp fix for ios continue video after fullscreen
    if (IS_IOS && state.playedSeconds) {
      dispatch(seekTo(state.playedSeconds))
    }
    onReady?.()
  }

  const handleError = useCallback(() => {
    onReady?.()
    if (state.playing) {
      dispatch(pause())
    }
  }, [dispatch, onReady, state.playing])

  // TODO: youtube video didn't trigger onPause on end
  const handleEnded = useCallback(() => {
    if (isYouTubeVideo(url)) {
      dispatch(pause())
    }
  }, [dispatch, url])

  // TODO: analytic need for onPause in unmount if remove state.playing running unmount every second (think about move to hook or rewrite)
  useUnmountMedia({ duration, playedSeconds: state.playedSeconds, playing: state.playing, onPause })

  useEffect(() => {
    const isBiggerThanViewport =
      (ref.current?.clientHeight || 0) > window.innerHeight ||
      (ref.current?.clientWidth || 0) > window.innerWidth

    if (
      inViewport &&
      (ratio === 1 || isBiggerThanViewport) &&
      autoPlay &&
      !state.started &&
      duration > 0
    ) {
      // TODO: when autoPlay is true, mute youtube video to allow auto play in browser after reload page
      if (isYouTubeVideo(url)) {
        dispatch(setVolume(0))
      }
      dispatch(play())
    }

    if (inViewport === false && duration > 0) {
      dispatch(pause())
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inViewport, ratio, autoPlay, state.started, duration, state.playing])

  useEffect(() => {
    if (isEditor) {
      dispatch(update({ volume, playbackRate, start, loop }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditor, volume, start, loop, playbackRate])

  useEffect(() => {
    return () => {
      if (state.playing) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearActiveMediaPlayer?.(ref.current)
      }
    }
  }, [clearActiveMediaPlayer, state.playing])

  useEffect(() => {
    const update = async () => {
      if (!captions || isSource) {
        return
      }

      blob = await encodeSilentBuffer(duration)
    }
    update()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, url, captions])

  useEffect(() => {
    if (!url && state.showCc) {
      dispatch(showCc())
    }
  }, [url, state.showCc, dispatch])

  useEffect(() => {
    if (onSavePlaybackPosition && state.maxPlayedSeconds != null) {
      onSavePlaybackPosition(state.maxPlayedSeconds)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.maxPlayedSeconds])

  const disableSeekingActive = !isEditor && disableSeeking

  const defaultPlayerConfig = {
    youtube: {
      // TODO: tmp fix for ios stop player after fullscreen if video is unstarted
      onUnstarted: () => IS_IOS && dispatch(pause()),
      ...(disableSeekingActive && { playerVars: { controls: 0 } }),
    },
  }

  useEffect(() => {
    if (!screenfull.isEnabled) {
      return
    }

    const onChange = () => {
      dispatch(setFullscreen(screenfull.isFullscreen))
    }

    screenfull.on('change', onChange)
    return () => screenfull.off('change', onChange)
  }, [dispatch])

  const playerFileConfig = { ...defaultPlayerConfig, ...(isSource ? ccData : {}) }

  const ReactPlayerComponent = (
    <div
      aria-label={type === FileTypeEnum.VIDEO ? 'Video player' : 'Audio player'}
      className={cn(s.root, { [s.fullscreen]: state.fullscreen, [s.full]: full })}
      onKeyDown={handleKeyDown}
      ref={ref}
      role='region'
      style={mainStyles}
      tabIndex={-1}
    >
      <div className={s.fixSwipe}>
        <div
          className={cn(s[type], s[device], {
            [s.fullscreen]: state.fullscreen,
            [s.full]: full,
            [s.showCC]: state.showCc,
          })}
          onClick={handleTogglePlay}
          style={{
            borderRadius: full ? (isNaN(innerBorderRadius) ? 2 : innerBorderRadius) : undefined,
          }}
        >
          <ReactPlayer
            config={playerFileConfig}
            height='100%'
            key={`${captions} ${url}`}
            onDuration={onDurationChange}
            onEnded={handleEnded}
            onError={handleError}
            onPause={handlePause}
            onPlay={handlePlay}
            onProgress={handleProgress}
            onReady={handleOnReady}
            playbackRate={state.playbackRate}
            playing={state.playing}
            // https://www.notion.so/1179-8434fc9b1d534581819ef9861a34facb?pvs=23
            progressInterval={100}
            ref={player}
            // eslint-disable-next-line react/forbid-component-props
            style={{ position: 'relative', ...(isV2 && { display: 'flex' }) }}
            url={url || '//'}
            volume={state.muted ? 0 : state.volume}
            width='100%'
            playsinline
          />
          {sourceType === FileUsageSource.url && captions && (
            <div className={s.ccPlayer}>
              <ReactPlayer
                config={ccData}
                height='100%'
                key={`${captions} ${url}`}
                playbackRate={state.playbackRate}
                playing={state.playing}
                // https://www.notion.so/1179-8434fc9b1d534581819ef9861a34facb?pvs=23
                progressInterval={100}
                ref={ccPayerRef}
                // eslint-disable-next-line react/forbid-component-props
                style={ST}
                url={blob}
                width='100%'
                playsinline
              />
            </div>
          )}
          {showCover && (
            <img
              alt={accessibility || 'Cover image'}
              aria-label={accessibility}
              className={s.cover}
              src={cover?.path || ''}
              style={{ objectFit: cover?.objectFit as CSSProperties['objectFit'] }}
            />
          )}
          {showMask && type === FileTypeEnum.VIDEO && (
            <div className={s.mask} style={{ backgroundColor: cover?.overlay }}>
              <PlayStyled
                {...testProps({ el: 'playMedia' })}
                $full={full}
                $styles={styles as ElementStyleCss<VideoSchemaType>}
                aria-label='Play video'
                data-player-control={MediaControlType.PlayMask}
              >
                <Play aria-hidden />
              </PlayStyled>
            </div>
          )}
        </div>
        {controls &&
          (device === 'mobile' && full ? (
            <MobileControls
              disableSeeking={!isEditor && disableSeeking}
              dispatch={dispatch}
              displayPlayback={displayPlayback}
              duration={duration}
              fonts={fonts}
              handleFullScreenIos={handleFullScreenIos}
              handleTogglePlay={handleTogglePlay}
              hasCC={Boolean(captions)}
              isCourse={isCourse}
              isEditor={isEditor}
              maxPlayedSeconds={state.maxPlayedSeconds}
              state={state}
              styles={styles}
              type={type}
              urlLoading={!duration && Boolean(url)}
              {...rest}
            />
          ) : (
            <Controls
              disableSeeking={!isEditor && disableSeeking}
              dispatch={dispatch}
              displayPlayback={displayPlayback}
              duration={duration}
              fonts={fonts}
              full={full}
              handleFullScreenIos={handleFullScreenIos}
              handleTogglePlay={handleTogglePlay}
              hasCC={Boolean(captions)}
              isCourse={isCourse}
              isEditor={isEditor}
              maxPlayedSeconds={state.maxPlayedSeconds}
              state={state}
              styles={styles}
              type={type}
              urlLoading={!duration && Boolean(url)}
              {...rest}
            />
          ))}
      </div>
    </div>
  )

  return iosFullScreen && IS_IOS
    ? createPortal(<div className={s.wrapIos}>{ReactPlayerComponent}</div>, document.body)
    : ReactPlayerComponent
}
export default MediaPlayer
